import { useState, useRef, useCallback, useEffect } from "react";

export interface DragState {
  isDragging: boolean;
  draggedIndex: number | null;
  currentY: number;
  startY: number;
  offsetY: number;
  initialIndex: number | null;
}

interface UseDragAndDropOptions<T> {
  items: T[];
  onReorder: (newItems: T[]) => void;
  longPressDuration?: number;
  enableDragHandle?: boolean;
}

export const useDragAndDrop = <T,>({
  items,
  onReorder,
  longPressDuration = 200,
  enableDragHandle = false,
}: UseDragAndDropOptions<T>) => {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedIndex: null,
    currentY: 0,
    startY: 0,
    offsetY: 0,
    initialIndex: null,
  });

  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rafIdRef = useRef<number | null>(null);

  // 드래그 중인 아이템의 새 인덱스 계산 (간단한 버전)
  const getNewIndex = useCallback(
    (draggedIndex: number, offsetY: number): number => {
      if (Math.abs(offsetY) < 10) {
        return draggedIndex; // 작은 이동은 무시
      }

      const draggedHeight = itemRefs.current[draggedIndex]?.offsetHeight || 0;
      const threshold = draggedHeight / 2;

      let newIndex = draggedIndex;

      if (offsetY < -threshold) {
        // 위로 드래그
        let cumulativeHeight = 0;
        for (let i = draggedIndex - 1; i >= 0; i--) {
          const element = itemRefs.current[i];
          if (!element) continue;
          cumulativeHeight += element.offsetHeight;
          if (Math.abs(offsetY) > cumulativeHeight - element.offsetHeight / 2) {
            newIndex = i;
          } else {
            break;
          }
        }
      } else if (offsetY > threshold) {
        // 아래로 드래그
        let cumulativeHeight = 0;
        for (let i = draggedIndex + 1; i < items.length; i++) {
          const element = itemRefs.current[i];
          if (!element) continue;
          const itemHeight = element.offsetHeight;
          cumulativeHeight += itemHeight;
          // 다음 아이템의 중간 지점을 넘으면 그 아이템 다음 위치로 이동
          // (그 아이템 뒤에 삽입)
          if (offsetY > cumulativeHeight - itemHeight / 2) {
            newIndex = i + 1;
          } else {
            break;
          }
        }
      }

      return Math.max(0, Math.min(newIndex, items.length - 1));
    },
    [items.length]
  );

  // Long press 시작
  const handleTouchStart = useCallback(
    (
      index: number,
      event: React.TouchEvent | React.PointerEvent,
      isHandle: boolean = false
    ) => {
      // 드래그 핸들을 사용하는 경우, 핸들이 아닌 곳에서 시작하면 무시
      if (enableDragHandle && !isHandle) {
        return;
      }

      const touch =
        "touches" in event
          ? event.touches[0]
          : { clientY: (event as React.PointerEvent).clientY };

      const startY = touch.clientY;

      // Long press 타이머 설정
      longPressTimerRef.current = setTimeout(() => {
        setDragState({
          isDragging: true,
          draggedIndex: index,
          currentY: startY,
          startY: startY,
          offsetY: 0,
          initialIndex: index,
        });

        // 스크롤 방지
        if (containerRef.current) {
          containerRef.current.style.overflow = "hidden";
        }

        // 햅틱 피드백 (가능한 경우)
        if (navigator.vibrate) {
          navigator.vibrate(10);
        }
      }, longPressDuration);

      // preventDefault로 기본 동작 방지
      event.preventDefault();
    },
    [longPressDuration, enableDragHandle]
  );

  // 드래그 중
  const handleTouchMove = useCallback(
    (event: TouchEvent | PointerEvent) => {
      if (!dragState.isDragging || dragState.draggedIndex === null) {
        return;
      }

      const touch =
        "touches" in event
          ? event.touches[0]
          : { clientY: (event as PointerEvent).clientY };

      const currentY = touch.clientY;
      const offsetY = currentY - dragState.startY;

      // 수직 이동만 허용 (x는 0으로 고정)
      setDragState((prev) => ({
        ...prev,
        currentY,
        offsetY,
      }));

      // requestAnimationFrame으로 부드러운 업데이트
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }

      rafIdRef.current = requestAnimationFrame(() => {
        // 아이템 위치 업데이트는 컴포넌트에서 처리
      });

      event.preventDefault();
    },
    [dragState.isDragging, dragState.draggedIndex, dragState.startY]
  );

  // 드래그 종료
  const handleTouchEnd = useCallback(() => {
    // Long press 타이머 취소
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    if (!dragState.isDragging || dragState.draggedIndex === null) {
      return;
    }

    const { draggedIndex, offsetY, initialIndex } = dragState;

    // 새 인덱스 계산
    const newIndex = getNewIndex(draggedIndex, offsetY);

    // 순서 변경이 있었는지 확인
    if (initialIndex !== null && initialIndex !== newIndex) {
      // 배열 재정렬
      const newItems = [...items];
      const [removed] = newItems.splice(initialIndex, 1);
      // newIndex는 제거 전 배열 기준의 "다음 위치"를 의미 (i + 1)
      // 아래로 이동하는 경우: 제거 후 인덱스가 하나씩 앞당겨지므로 newIndex - 1
      // 위로 이동하는 경우: newIndex는 그대로 사용
      let insertIndex = newIndex;
      if (initialIndex < newIndex) {
        // 아래로 이동: newIndex는 "다음 위치"이므로 제거 후에는 newIndex - 1
        insertIndex = newIndex - 1;
      }
      // 배열 길이를 초과하지 않도록 제한
      insertIndex = Math.min(insertIndex, newItems.length);
      newItems.splice(Math.max(0, insertIndex), 0, removed);
      onReorder(newItems);
    }

    // 드래그 상태 초기화
    setDragState({
      isDragging: false,
      draggedIndex: null,
      currentY: 0,
      startY: 0,
      offsetY: 0,
      initialIndex: null,
    });

    // 스크롤 복원
    if (containerRef.current) {
      containerRef.current.style.overflow = "";
    }
  }, [dragState, items, getNewIndex, onReorder]);

  // 전역 이벤트 리스너 등록
  useEffect(() => {
    if (dragState.isDragging) {
      const handleMove = (e: TouchEvent | PointerEvent) => handleTouchMove(e);
      const handleEnd = () => handleTouchEnd();

      window.addEventListener("touchmove", handleMove, { passive: false });
      window.addEventListener("touchend", handleEnd);
      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleEnd);
      window.addEventListener("pointercancel", handleEnd);

      return () => {
        window.removeEventListener("touchmove", handleMove);
        window.removeEventListener("touchend", handleEnd);
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleEnd);
        window.removeEventListener("pointercancel", handleEnd);
      };
    }
  }, [dragState.isDragging, handleTouchMove, handleTouchEnd]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return {
    dragState,
    handleTouchStart,
    itemRefs,
    containerRef,
  };
};

